'use client';
import React, { useState, useCallback } from 'react';
import {
  Code2, BarChart2, Brain,
  ChevronDown, Sparkles,
  BookOpen, Clock, Star,
} from 'lucide-react';

interface Subject {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  topicCount: number;
  estimatedHours: number;
  tags: string[];
  isEntryPoint?: boolean;
}

interface Track {
  id: string;
  title: string;
  subtitle: string;
  narrativeText: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
  accentHex: string;
  subjects: Subject[];
  recommendedPath: string[];
  pathLabel: string;
  pathNarrative: string;
}

const TRACKS: Track[] = [
  {
    id: 'track-available',
    title: 'Available Now',
    subtitle: 'Subjects with full session content ready to start',
    narrativeText: 'These are the subjects currently available for live learning sessions. Each has complete topic content, adaptive teaching styles, and quizzes.',
    icon: Brain,
    color: 'text-primary',
    bgColor: 'bg-primary/5',
    borderColor: 'border-primary/30',
    accentHex: '#E8590C',
    recommendedPath: ['subj-python', 'subj-stats', 'subj-philosophy'],
    pathLabel: 'Python → Statistics → Philosophy of Mind',
    pathNarrative: 'Start with Python to build computational thinking, move into Statistics for data reasoning, then explore Philosophy of Mind for deeper conceptual grounding.',
    subjects: [
      {
        id: 'subj-python',
        title: 'Python Programming',
        description: 'Variables, conditionals, loops, and functions — the core building blocks of programming, taught from first principles.',
        icon: Code2,
        topicCount: 4,
        estimatedHours: 3,
        tags: ['Variables', 'Logic', 'Functions'],
        isEntryPoint: true,
      },
      {
        id: 'subj-stats',
        title: 'Statistics',
        description: 'Mean, median, mode, and standard deviation — the essential tools for understanding and interpreting data.',
        icon: BarChart2,
        topicCount: 2,
        estimatedHours: 2,
        tags: ['Data', 'Distributions', 'Inference'],
      },
      {
        id: 'subj-philosophy',
        title: 'Philosophy of Mind',
        description: 'The hard problem of consciousness, dualism vs physicalism — foundational questions about the nature of mind and experience.',
        icon: Brain,
        topicCount: 2,
        estimatedHours: 2,
        tags: ['Consciousness', 'Dualism', 'Physicalism'],
      },
    ],
  },
];

interface LearningTracksProps {
  selectedSubject: string | null;
  onSelectSubject: (id: string) => void;
}

export default function LearningTracks({ selectedSubject, onSelectSubject }: LearningTracksProps) {
  const [expandedTrack, setExpandedTrack] = useState<string | null>('track-available');
  const [hoveredSubject, setHoveredSubject] = useState<string | null>(null);

  const toggleTrack = useCallback((trackId: string) => {
    setExpandedTrack((prev) => (prev === trackId ? null : trackId));
  }, []);

  return (
    <div className="space-y-3">
      {TRACKS.map((track) => {
        const TrackIcon = track.icon;
        const isExpanded = expandedTrack === track.id;
        const trackSelected = track.subjects.some((s) => s.id === selectedSubject);

        return (
          <div
            key={track.id}
            className={`rounded-2xl border-2 overflow-hidden transition-all duration-300 ${
              trackSelected
                ? `${track.borderColor} shadow-sm`
                : isExpanded
                ? 'border-border shadow-sm'
                : 'border-border'
            }`}
          >
            {/* Track Header */}
            <button
              onClick={() => toggleTrack(track.id)}
              className={`w-full flex items-center justify-between px-5 py-4 transition-colors duration-200 ${
                isExpanded ? 'bg-card' : 'bg-card hover:bg-muted/40'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${track.bgColor} ${track.color}`}
                >
                  <TrackIcon size={18} />
                </div>
                <div className="text-left min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {trackSelected && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${track.bgColor} ${track.color}`}>
                        Selected
                      </span>
                    )}
                  </div>
                  <h3 className="text-sm font-bold text-foreground leading-tight">{track.title}</h3>
                  {isExpanded ? (
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed max-w-sm">
                      {track.narrativeText}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-0.5 hidden sm:block">
                      {track.subtitle}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                <span className="text-xs text-muted-foreground hidden md:block">
                  {track.subjects.length} subjects
                </span>
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-200 ${
                    isExpanded ? 'bg-muted rotate-0' : 'bg-transparent'
                  }`}
                >
                  <ChevronDown
                    size={16}
                    className={`text-muted-foreground transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                  />
                </div>
              </div>
            </button>

            {/* Expanded Content */}
            {isExpanded && (
              <div className="border-t border-border">
                {/* Path Narrative Banner */}
                <div className={`px-5 py-3 ${track.bgColor} border-b border-border/50`}>
                  <div className="flex items-start gap-2">
                    <Sparkles size={13} className={`${track.color} mt-0.5 flex-shrink-0`} />
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="text-xs font-semibold text-muted-foreground">Suggested path:</span>
                        <span className={`text-xs font-semibold ${track.color}`}>{track.pathLabel}</span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">{track.pathNarrative}</p>
                    </div>
                  </div>
                </div>

                {/* Subjects */}
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {track.subjects.map((subject) => {
                    const SubjectIcon = subject.icon;
                    const isSelected = selectedSubject === subject.id;
                    const isHovered = hoveredSubject === subject.id;
                    const pathPosition = track.recommendedPath.indexOf(subject.id);
                    const showDetails = isHovered || isSelected;

                    return (
                      <div
                        key={subject.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => onSelectSubject(subject.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSelectSubject(subject.id);
                          }
                        }}
                        onMouseEnter={() => setHoveredSubject(subject.id)}
                        onMouseLeave={() => setHoveredSubject(null)}
                        className={`relative text-left p-4 rounded-xl border-2 bg-card transition-all duration-200 group cursor-pointer ${
                          isSelected
                            ? `${track.borderColor} ring-2 ring-offset-1 shadow-sm`
                            : `border-border hover:border-primary/30 hover:shadow-sm`
                        }`}
                      >
                        {/* Entry cue */}
                        {subject.isEntryPoint && !selectedSubject && (
                          <div className={`absolute -top-px left-4 px-2 py-0.5 rounded-b-md text-xs font-semibold ${track.bgColor} ${track.color} border-x border-b ${track.borderColor}`}>
                            <span className="flex items-center gap-1">
                              <Star size={9} />
                              Start here
                            </span>
                          </div>
                        )}

                        {/* Path position badge */}
                        {pathPosition >= 0 && (
                          <span
                            className={`absolute top-3 right-3 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${
                              isSelected ? `${track.bgColor} ${track.color}` : 'bg-muted text-muted-foreground'
                            }`}
                          >
                            {pathPosition + 1}
                          </span>
                        )}

                        <div className="flex items-start gap-3 mb-2.5 pr-6">
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                              isSelected
                                ? `${track.bgColor} ${track.color}`
                                : 'bg-muted text-muted-foreground group-hover:bg-muted/80'
                            }`}
                          >
                            <SubjectIcon size={15} />
                          </div>
                          {isSelected && (
                            <span
                              className="absolute top-3 left-3 w-4 h-4 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: track.accentHex }}
                            >
                              <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                                <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </span>
                          )}
                        </div>

                        <h4 className="text-sm font-semibold text-foreground mb-1 leading-tight">
                          {subject.title}
                        </h4>
                        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                          {subject.description}
                        </p>

                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2.5">
                          <span className="flex items-center gap-1">
                            <BookOpen size={10} />
                            {subject.topicCount} topics
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={10} />
                            ~{subject.estimatedHours}h
                          </span>
                        </div>

                        {/* Progressive reveal: tags shown only on hover/select */}
                        {showDetails && (
                          <div className="flex flex-wrap gap-1 animate-in fade-in duration-150">
                            {subject.tags.map((tag) => (
                              <span
                                key={`${subject.id}-${tag}`}
                                className={`px-1.5 py-0.5 rounded-full text-xs font-medium transition-colors ${
                                  isSelected ? `${track.bgColor} ${track.color}` : 'bg-muted text-muted-foreground'
                                }`}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export { TRACKS };
export type { Subject, Track };
